"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import type { LinkProps } from "next/link";
import type { AnchorHTMLAttributes, MouseEvent } from "react";

type TurboLinkProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & {
    children?: React.ReactNode;
    traceLabel?: string;
  };

const NAV_TRACE_QUERY_PARAM = "traceSidebarNav";
const NAV_TRACE_PENDING_KEY = "traceSidebarNavPending";
const NAV_TRACE_TIMEOUT_MS = 400;

function isNavTraceEnabled() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(NAV_TRACE_QUERY_PARAM) === "1";
}

function logNavTrace(label: string, step: string, payload: Record<string, unknown>) {
  if (!isNavTraceEnabled()) return;
  console.info("[sidebar-nav-trace]", { label, step, ...payload });
}

function buildClickPayload(event: MouseEvent<HTMLAnchorElement>) {
  return {
    button: event.button,
    defaultPrevented: event.defaultPrevented,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    targetTag:
      event.target instanceof HTMLElement ? event.target.tagName.toLowerCase() : null,
    currentTargetTag: event.currentTarget.tagName.toLowerCase(),
  };
}

export const TurboLink = (props: TurboLinkProps) => {
  const { traceLabel, onClick, onClickCapture, ...linkProps } = props;
  const router = useRouter();
  const pathname = usePathname();
  const strHref = typeof linkProps.href === "string" ? linkProps.href : linkProps.href.href;
  const pendingTraceIdRef = useRef<string | null>(null);

  const conditionalPrefetch = () => {
    if (strHref) {
      void router.prefetch(strHref);
    }
  };

  useEffect(() => {
    if (!traceLabel || !isNavTraceEnabled()) return;
    const rawPending = window.sessionStorage.getItem(NAV_TRACE_PENDING_KEY);
    if (!rawPending) return;

    try {
      const pending = JSON.parse(rawPending) as {
        traceId?: string;
        label?: string;
        pathnameBefore?: string;
        href?: string;
      };

      if (!pending.traceId || pending.traceId !== pendingTraceIdRef.current) return;
      if (pending.label !== traceLabel) return;

      const pathnameChanged = pending.pathnameBefore !== pathname;
      logNavTrace(traceLabel, "pathname-settled", {
        href: pending.href ?? strHref,
        pathnameBefore: pending.pathnameBefore ?? null,
        pathnameAfter: pathname,
        pathnameChanged,
      });

      if (pathnameChanged) {
        window.sessionStorage.removeItem(NAV_TRACE_PENDING_KEY);
        pendingTraceIdRef.current = null;
      }
    } catch {
      window.sessionStorage.removeItem(NAV_TRACE_PENDING_KEY);
      pendingTraceIdRef.current = null;
    }
  }, [pathname, strHref, traceLabel]);

  return (
    <Link
      {...linkProps}
      prefetch={false}
      onClickCapture={(e) => {
        if (traceLabel) {
          logNavTrace(traceLabel, "click-capture", {
            href: strHref,
            pathname,
            ...buildClickPayload(e),
          });
        }
        return onClickCapture?.(e);
      }}
      onClick={(e) => {
        const navTraceEnabled = traceLabel ? isNavTraceEnabled() : false;
        const traceLogLabel = traceLabel ?? strHref ?? pathname;
        const trackingNavigation =
          !e.defaultPrevented &&
          e.button === 0 &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey &&
          (!linkProps.target || linkProps.target === "_self");

        if (traceLabel) {
          logNavTrace(traceLabel, "click-before", {
            href: strHref,
            pathname,
            trackingNavigation,
            ...buildClickPayload(e),
          });
        }

        const result = onClick?.(e);

        if (traceLabel) {
          logNavTrace(traceLabel, "click-after", {
            href: strHref,
            pathname,
            trackingNavigation,
            ...buildClickPayload(e),
          });
        }

        if (navTraceEnabled) {
          if (!trackingNavigation || e.defaultPrevented) {
            pendingTraceIdRef.current = null;
            window.sessionStorage.removeItem(NAV_TRACE_PENDING_KEY);
            logNavTrace(traceLogLabel, "navigation-not-tracked", {
              href: strHref,
              pathname,
              trackingNavigation,
              defaultPrevented: e.defaultPrevented,
            });
            return result;
          }

          const traceId = `${traceLabel}:${Date.now()}`;
          pendingTraceIdRef.current = traceId;
          window.sessionStorage.setItem(
            NAV_TRACE_PENDING_KEY,
            JSON.stringify({
              traceId,
              label: traceLabel,
              href: strHref,
              pathnameBefore: pathname,
            }),
          );

          window.setTimeout(() => {
            if (!traceLabel || pendingTraceIdRef.current !== traceId) return;
            const rawPending = window.sessionStorage.getItem(NAV_TRACE_PENDING_KEY);
            if (!rawPending) return;

            try {
              const pending = JSON.parse(rawPending) as {
                traceId?: string;
                pathnameBefore?: string;
                href?: string;
              };
              if (pending.traceId !== traceId) return;

              logNavTrace(traceLogLabel, "navigation-timeout", {
                href: pending.href ?? strHref,
                pathnameBefore: pending.pathnameBefore ?? pathname,
                pathnameAfter: window.location.pathname,
                pathnameChanged:
                  window.location.pathname !== (pending.pathnameBefore ?? pathname),
              });
            } catch {
              window.sessionStorage.removeItem(NAV_TRACE_PENDING_KEY);
              pendingTraceIdRef.current = null;
            }
          }, NAV_TRACE_TIMEOUT_MS);
        }

        return result;
      }}
      onMouseEnter={(e) => {
        conditionalPrefetch();
        return linkProps.onMouseEnter?.(e);
      }}
      onPointerEnter={(e) => {
        conditionalPrefetch();
        return linkProps.onPointerEnter?.(e);
      }}
      onTouchStart={(e) => {
        conditionalPrefetch();
        return linkProps.onTouchStart?.(e);
      }}
      onFocus={(e) => {
        conditionalPrefetch();
        return linkProps.onFocus?.(e);
      }}
    >
      {linkProps.children}
    </Link>
  );
};
